import React, { useEffect } from 'react';
import { TextInput, DateInput, ReferenceInput, AutocompleteInput, FormDataConsumer, useRecordContext, useGetOne } from 'react-admin';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';

import InvoiceLineForm from './InvoiceLineForm';
import CustomerReferenceInput from './inputs/CustomerReferenceInput';

const optionText = choice => choice.name ? choice.name : '';


const gridReferenceSx = (theme) => ({
	order: 0,
	[theme.breakpoints.down('sm')]: {
		order: 2,
		marginTop: '15px'
	}
});

const gridTableSx = (theme) => ({
	[theme.breakpoints.down('sm')]: {
		maxWidth: 'calc(100vw - 80px)'
	},
	[theme.breakpoints.down('xs')]: {
		maxWidth: '100vw'
	},
	'.MuiTableCell-root .MuiFormControl-root': {
		minWidth: 'auto !important',
		width: '100%'
	}
});

const InvoiceDetails = ({ record, id }) => {
	const { data: company } = useGetOne('companies', { id });

	return (
		<Paper align="center" variant="outlined">
			<Typography variant="h6">IBAN</Typography>
			<Typography>{record?.bank_iban || company?.bank_iban}</Typography>

			<Typography variant="h6">BIC</Typography>
			<Typography>{record?.bank_bic || company?.bic}</Typography>

			{record?.structured_reference && (
				<>
					<Typography variant="h6">Reference to communicate</Typography>
					<Typography>+++{record?.structured_reference}+++</Typography>
				</>
			)}
		</Paper>
	);
};

const TotalTable = ({ vatList, lines }) => {
	let total = 0;

	if (lines) {
		total = lines.reduce((acc, line) => acc + (line.qty * line.unit_price || 0), 0);
	}

	let totalVat = total;
	const vatListTr = vatList.map(vat => {
		if (!lines) {
			return null;
		}

		const value = lines.filter(line => line.vat_code === vat.id).reduce((acc, line) => acc + ((line.qty * line.unit_price * vat.rate / 100) || 0), 0);
		totalVat += value;

		if (value) {
			return (
				<TableRow key={`vat_${vat.id}`}>
					<TableCell>{vat.name}</TableCell>
					<TableCell>{value.toFixed(2)} &euro;</TableCell>
				</TableRow>
			);
		}

		return null;
	});

	return (
		<Table>
			<TableBody>
				<TableRow>
					<TableCell><b>Total excl. VAT</b></TableCell>
					<TableCell>{total.toFixed(2)} &euro;</TableCell>
				</TableRow>
				{vatListTr}
				<TableRow>
					<TableCell><b>Total incl. VAT</b></TableCell>
					<TableCell><b>{totalVat.toFixed(2)} &euro;</b></TableCell>
				</TableRow>
			</TableBody>
		</Table>
	);
};

const InvoiceForm = ({
	vatList = [
		{ id: 'vat_0', name: '0%', rate: 0 },
		{ id: 'vat_21', name: '21%', rate: 21 },
		{ id: 'vat_20', name: '20%', rate: 20 },
		{ id: 'vat_intra_goods_0', name: 'INTRA GOODS 0%', rate: 0 },
		{ id: 'vat_intra_services_0', name: 'INTRA SERVICES 0%', rate: 0 },
		{ id: 'vat_export_goods_0', name: 'EXPORT GOODS 0%', rate: 0 },
		{ id: 'vat_export_services_0', name: 'EXPORT SERVICES 0%', rate: 0 },
		{ id: 'vat_not_subject', name: 'NA', rate: 0 }
	],
	draggable = true
}) => {
	const record = useRecordContext();
	const { control, register } = useFormContext();
	const { fields, append, remove, swap } = useFieldArray({ control, name: 'lines' });

	useEffect(() => {
		if (!record?.id || (record?.lines && record?.lines.length === 0)) {
			addLine();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addLine = () => {
		append({})
	};

	const onDragEnd = result => {
		if (!result.destination) {
			return;
		}
		swap(result.source.index, result.destination.index);
	};

	const invoiceLines = (
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable droppableId="droppable">
				{(provided) => (
					<TableBody ref={provided.innerRef}>
						{fields.map((field, index) => (
							<Draggable
								key={field.id}
								draggableId={field.id}
								index={index}
							>
								{(provided) => (
									<TableRow
										ref={provided.innerRef}
										{...provided.draggableProps}
										{...provided.dragHandleProps}
									>
										<InvoiceLineForm
											index={index}
											draggable={draggable}
											vatList={vatList}
											name={field.id}
											remove={remove}
											register={register}
										/>
									</TableRow>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</TableBody>
				)}
			</Droppable>
		</DragDropContext>
	);

	return (
		<Box width="100%">
			<Typography variant="h4" gutterBottom>Informations</Typography>
			<Grid container direction="row" spacing={3}>
				<Grid size={{ xs: 8, md: 8 }}>
					<ReferenceInput sort={{ field: 'name', order: 'ASC' }} source="billing_company_id" reference="companies" allowEmpty helperText={false}>
						<AutocompleteInput optionText={optionText} />
					</ReferenceInput>
					<CustomerReferenceInput />
				</Grid>
				<Grid size={{ xs: 4, md: 4 }} display="flex" flexDirection="column">
					<TextInput source="number" />
					<TextInput source="purchase_order" />
					<DateInput source="invoiced_at" />
					<DateInput source="due_date" />
				</Grid>
			</Grid>
			<Grid size={{ xs: 12 }} sx={gridTableSx}>
				<Typography variant="h4">Description</Typography>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{draggable && <TableCell></TableCell>}
								<TableCell>Description</TableCell>
								<TableCell>Quantity</TableCell>
								<TableCell>Price</TableCell>
								<TableCell>VAT</TableCell>
								<TableCell>Total excl. VAT</TableCell>
								<TableCell align="center">
									<Button color="primary" variant="outlined" size="small" onClick={addLine}><AddIcon />Add</Button>
								</TableCell>
							</TableRow>
						</TableHead>
						{invoiceLines}
					</Table>
				</TableContainer>
			</Grid>
			<Grid>
				<Grid container justifyContent="space-between" py={3}>
					<Grid size={{ md: 7, xs: 12 }} sx={gridReferenceSx}>
						<FormDataConsumer>
							{({ formData }) => (
								<InvoiceDetails record={record} id={formData.billing_company_id} />
							)}
						</FormDataConsumer>
					</Grid>
					<Grid size={{ md: 4, xs: 12 }}>
						<FormDataConsumer>
							{({ formData }) => (
								formData.lines && <TotalTable vatList={vatList} lines={Object.values(formData.lines)} />
							)}
						</FormDataConsumer>
					</Grid>
				</Grid>
			</Grid>
			<Grid>
				<Grid container spacing={2}>
					<Grid size={{ md: 6, xs: 12 }}>
						<Typography variant="h5">Terms and conditions of payment</Typography>
						<TextInput label="" source="terms" multiline rows="4" fullWidth sx={{ maxWidth: '100%' }} />
					</Grid>
					<Grid size={{ md: 6, xs: 12 }}>
						<Typography variant="h5">Notes</Typography>
						<TextInput label="" source="notes" multiline rows="4" fullWidth sx={{ maxWidth: '100%' }} />
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};

export default InvoiceForm;